/**
 * 1-on-1 state, derived - never stored.
 *
 * Same reason an assessment stores `status` and derives its stage: a stored state
 * beside the three timestamps that produce it is a fourth thing to keep in step,
 * and when they disagree there is no way to tell which one lied.
 *
 * The screen used to read `acknowledgedAt` alone, which gave two states for four
 * situations. A meeting three weeks out and a meeting whose notes were never
 * written both showed "Awaiting acknowledgement" in amber - the first is not
 * waiting on anybody, and the second is waiting on the MANAGER. Pointing the
 * amber pill at the employee for their manager's unwritten notes is exactly the
 * kind of quiet unfairness the audit rule exists to prevent.
 */
export type OneOnOneState =
  | 'SCHEDULED'
  | 'AWAITING_NOTES'
  | 'AWAITING_ACKNOWLEDGEMENT'
  | 'ACKNOWLEDGED'
  | 'NOT_HELD';

/**
 * Only the fields the state depends on. `Date | string` because the API holds
 * instants and the SPA receives ISO strings over JSON, and one rule has to answer
 * for both.
 */
export interface OneOnOneStateInput {
  scheduledAt: Date | string;
  notes: string | null;
  acknowledgedAt: Date | string | null;
  /** Set once somebody records that the conversation never took place. */
  notHeldAt?: Date | string | null;
}

/**
 * `now` is injected rather than read inside, so the boundary is testable.
 *
 * Compared as instants, not PlainDates: `scheduledAt` is the timestamp of an event
 * ("2:30pm on the 30th"), not a day-level window boundary, so there is no
 * Asia/Kolkata conversion to get wrong here - both sides are absolute moments.
 */
export function oneOnOneState(
  meeting: OneOnOneStateInput,
  now: Date = new Date(),
): OneOnOneState {
  // First, and regardless of date: an acknowledged meeting is settled. A record
  // both people signed off is not reopened by the clock moving.
  if (meeting.acknowledgedAt) return 'ACKNOWLEDGED';

  // Before the date check, so a meeting marked not held reads that way even if
  // somebody later moves it - and after ACKNOWLEDGED, which the service refuses to
  // let this overwrite anyway.
  if (meeting.notHeldAt) return 'NOT_HELD';

  if (new Date(meeting.scheduledAt).getTime() > now.getTime()) return 'SCHEDULED';

  // Past its time, so somebody is late - and the notes say which one. Deliberately
  // not auto-closed as "missed": a 1-on-1 held in the corridor and written up two
  // days later is most of them, and stamping it missed on the clock's word would
  // manufacture false records that HR then has to undo.
  return meeting.notes && meeting.notes.trim() !== ''
    ? 'AWAITING_ACKNOWLEDGEMENT'
    : 'AWAITING_NOTES';
}
